import React, { useRef, useEffect, useCallback } from "react";
import {
  ServicesContainer,
  ServicesH1,
  ServicesWrapper,
  ServicesCard,
  ServicesIcon,
  ServicesH2,
  ServicesP,
} from "./ServicesElements";
import Icon1 from "../../images/space.svg";
import Icon2 from "../../images/online.svg";
import Icon3 from "../../images/real-time.svg";
import Icon4 from "../../images/design-notes.svg";
import Icon5 from "../../images/message.svg";
import Icon6 from "../../images/seo.svg";
import Icon7 from "../../images/bespoke.svg";
import Icon8 from "../../images/enterprise.svg";

const servicesData = [
  {
    icon: Icon1,
    title: "Branding",
    description:
      "Create a memorable brand identity that resonates with your audience. We design logos, brand guidelines, and visual assets that set you apart.",
  },
  {
    icon: Icon2,
    title: "Websites",
    description:
      "Custom websites built with modern technologies. From landing pages to complex web applications, we deliver responsive and SEO-friendly solutions.",
  },
  {
    icon: Icon3,
    title: "Digital Marketing",
    description:
      "Strategic digital marketing campaigns that drive traffic and conversions. We help you reach your target audience effectively.",
  },
  {
    icon: Icon4,
    title: "Web Designed",
    description:
      "Beautiful, user-centered designs that convert. We craft interfaces that are visually appealing and deliver exceptional user experiences.",
  },
  {
    icon: Icon5,
    title: "Social Media",
    description:
      "Engage your audience across all social platforms. We create compelling content strategies that build community and drive engagement.",
  },
  {
    icon: Icon6,
    title: "SEO",
    description:
      "Improve your search engine rankings and visibility. Our SEO strategies help you get found by customers searching for your services.",
  },
  {
    icon: Icon7,
    title: "Bespoke",
    description:
      "Tailored solutions designed specifically for your unique business needs. Custom development that fits your exact requirements.",
  },
  {
    icon: Icon8,
    title: "Enterprise",
    description:
      "Scalable enterprise solutions for large organizations. We build robust systems that support your business growth and operations.",
  },
];

const Services = () => {
  const scrollRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const isAutoScrollingRef = useRef(true);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const velocityRef = useRef(0);
  const lastXRef = useRef(0);
  const momentumRef = useRef(null);

  const startAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
    }

    autoScrollIntervalRef.current = setInterval(() => {
      if (scrollRef.current && isAutoScrollingRef.current) {
        const { scrollLeft: currentScrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        const maxScroll = scrollWidth - clientWidth;

        if (currentScrollLeft >= maxScroll - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          scrollRef.current.scrollBy({ left: 344, behavior: "smooth" });
        }
      }
    }, 3000);
  }, []);

  const stopAutoScroll = useCallback(() => {
    isAutoScrollingRef.current = false;
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
    }

    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }

    resumeTimeoutRef.current = setTimeout(() => {
      isAutoScrollingRef.current = true;
      startAutoScroll();
    }, 15000);
  }, [startAutoScroll]);

  const applyMomentum = useCallback(() => {
    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
    }

    const animate = () => {
      if (Math.abs(velocityRef.current) > 0.5 && scrollRef.current) {
        scrollRef.current.scrollLeft -= velocityRef.current;
        velocityRef.current *= 0.95;
        momentumRef.current = requestAnimationFrame(animate);
      }
    };

    momentumRef.current = requestAnimationFrame(animate);
  }, []);

  const handleMouseDown = useCallback((e) => {
    isDraggingRef.current = true;
    startXRef.current = e.pageX;
    scrollLeftRef.current = scrollRef.current.scrollLeft;
    lastXRef.current = e.pageX;
    velocityRef.current = 0;

    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
    }

    stopAutoScroll();
    document.body.style.cursor = 'grabbing';
  }, [stopAutoScroll]);

  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      applyMomentum();
    }
  }, [applyMomentum]);

  const handleMouseLeave = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      applyMomentum();
    }
  }, [applyMomentum]);

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();

    const x = e.pageX;
    const walk = x - startXRef.current;
    velocityRef.current = x - lastXRef.current;
    lastXRef.current = x;

    scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  }, []);

  const handleTouchStart = useCallback((e) => {
    isDraggingRef.current = true;
    startXRef.current = e.touches[0].pageX;
    scrollLeftRef.current = scrollRef.current.scrollLeft;
    lastXRef.current = e.touches[0].pageX;
    velocityRef.current = 0;

    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current);
    }

    stopAutoScroll();
  }, [stopAutoScroll]);

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current) return;

    const x = e.touches[0].pageX;
    const walk = x - startXRef.current;
    velocityRef.current = x - lastXRef.current;
    lastXRef.current = x;

    scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  }, []);

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false;
    applyMomentum();
  }, [applyMomentum]);

  const handleWheel = useCallback(() => {
    stopAutoScroll();
  }, [stopAutoScroll]);

  useEffect(() => {
    startAutoScroll();

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
      if (momentumRef.current) {
        cancelAnimationFrame(momentumRef.current);
      }
    };
  }, [startAutoScroll]);

  return (
    <ServicesContainer id="services">
      <ServicesH1>Our Services</ServicesH1>
      <ServicesWrapper
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
      >
        {servicesData.map((service, index) => (
          <ServicesCard key={index}>
            <ServicesIcon src={service.icon} alt={service.title} draggable={false} />
            <ServicesH2>{service.title}</ServicesH2>
            <ServicesP>{service.description}</ServicesP>
          </ServicesCard>
        ))}
      </ServicesWrapper>
    </ServicesContainer>
  );
};

export default Services;
